// src/components/settings/index.js
import React, { useEffect } from "react";
import { Routes, Route, Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import "./Settings.css";
import "./SettingsTheme.css";

import DocumentTypeList from "./DocumentTypeList";
import DocumentTypeEdit from "./DocumentTypeEdit";
import AssignFieldsToDocumentType from "./AssignFieldsToDocumentType";
import MetadataDefinitionList from "./DocumentMetadataDefinitionList";
import MetadataDefinitionEdit from "./DocumentMetadataDefinitionEdit";
import LanguageSettings from "./LanguageSettings";
import EndeavorMetadataDefinitionList from "./EndeavorMetadataDefinitionList";
import EndeavorMetadataDefinitionEdit from "./EndeavorMetadataDefinitionEdit";
import CreateEditEventType from "./CreateEditEventType";
import EndeavorTypeList from "./EndeavorTypeList";
import EndeavorTypeEdit from "./EndeavorTypeEdit";
import EventMetadataDefinitionList from "./EventMetadataDefinitionList";
import EventMetadataDefinitionEdit from "./EventMetadataDefinitionEdit";
import EventTypeList from "./EventTypeList";
import OrganizationList from "./OrganizationList";
import RoleList from "./RoleList";
import UserList from "./UserList";
import CreateUser from "./CreateUserPage";
import InviteUserPage from "./InviteUserPage";
import GptPromptEventsDashboard from "./GptPromptEventsDashboard"; // NEW: GPT events dashboard
import UsageEventsDashboard from "./UsageEventsDashboard"; // Generic usage events dashboard
import UserUsageEventsDashboard from "./UserUsageEventsDashboard"; // NEW: per-user usage events view
import PaymentsAdminDashboard from "./PaymentsAdminDashboard"; // NEW: Payments analytics dashboard
import AdminGrantProAccess from "./AdminGrantProAccess"; // NEW: Admin grant complimentary pro access
import EmailList from './EmailList'; // NEW: Email list
import EmailEdit from './EmailEdit'; // NEW: Email create/edit
import DataRetentionSettings from './DataRetentionSettings'; // NEW: Data retention admin
import AdminSystemStats from './AdminSystemStats'; // NEW: System stats / cache observability
import WebhookMonitor from './WebhookMonitor'; // NEW: Payment webhook monitoring
import AnalyticsOverview from './AnalyticsOverview'; // NEW: Unified analytics overview
import CorrelationDetail from './CorrelationDetail'; // NEW: Cross-service event correlation timeline
import ImpersonateUser from './ImpersonateUser'; // Admin impersonation page
import OrganizationMembersPage from './OrganizationMembersPage'; // Org members management
import UserOrganizationsPage from './UserOrganizationsPage'; // User org-membership management

// Admin-only pages:
import CreateOrganization from "./CreateOrganizationPage";
import CreateRole from "./CreateRolePage";

import ProtectedRoute from "../../components/ProtectedRoute";

function SettingsSection({ title, children }) {
  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <h3 style={{ fontSize: '.85rem', fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.75rem' }}>{title}</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.75rem' }}>
        {children}
      </div>
    </div>
  );
}

function SettingsCard({ to, icon, label }) {
  return (
    <Link
      to={to}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
        padding: '1rem 0.75rem', background: 'var(--card-bg, #fff)', border: '1px solid var(--border-subtle, #e2e8f0)',
        borderRadius: 'var(--radius-lg, 12px)', textDecoration: 'none', color: 'var(--text-primary)',
        boxShadow: 'var(--shadow-sm)', transition: 'all 0.2s', textAlign: 'center', minHeight: '90px'
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'var(--shadow-md)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; e.currentTarget.style.transform = 'none'; }}
    >
      <span style={{ fontSize: '1.5rem' }}>{icon}</span>
      <span style={{ fontSize: '.75rem', fontWeight: 600, lineHeight: 1.3 }}>{label}</span>
    </Link>
  );
}

function Settings() {
  const { user, isInitialized } = useAuth();

  useEffect(() => {
    if (isInitialized && user) {
      console.log('Settings: User loaded:', user);
      console.log('Settings: User roles:', user.roles);
      console.log('Settings: Is admin check:', user?.roles?.some(role => role.roleName === "admin"));
    }
  }, [isInitialized, user]);

  // Show loading state while auth is initializing
  if (!isInitialized) {
    return (
      <div className="settings-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  // Check if user has admin role
  const isAdmin =
    user &&
    user.roles &&
    Array.isArray(user.roles) &&
    user.roles.some((role) => role.roleName === "admin");

  console.log('Settings: isAdmin result:', isAdmin);

  // If user is not admin, show access denied message
  if (!isAdmin) {
    return (
      <div className="settings-container">
        <div className="access-denied">
          <div className="access-denied-content">
            <h2>🔒 Access Denied</h2>
            <p>You need administrator privileges to access the Settings panel.</p>
            <p>Please contact your system administrator if you believe you should have access.</p>
            <div style={{ marginTop: '2rem' }}>
              <button 
                onClick={() => window.history.back()}
                className="btn-secondary"
              >
                ← Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const SettingsHub = () => (
    <div className="settings-hub">
      <h2 style={{ marginTop: 0, marginBottom: '1.5rem' }}>Settings</h2>

      {/* Content Management */}
      <SettingsSection title="Content Management">
        <SettingsCard to="/settings/document-types" icon="📄" label="Document Types" />
        <SettingsCard to="/settings/document-metadata-definitions" icon="🏷️" label="Document Metadata" />
        <SettingsCard to="/settings/endeavor-types" icon="🎓" label="Endeavor Types" />
        <SettingsCard to="/settings/endeavor-metadata-definitions" icon="📋" label="Endeavor Metadata" />
        <SettingsCard to="/settings/event-types" icon="📅" label="Event Types" />
        <SettingsCard to="/settings/event-metadata-definitions" icon="🗂️" label="Event Metadata" />
        <SettingsCard to="/settings/reminder-policies" icon="⏰" label="Reminder Policies" />
      </SettingsSection>

      {/* Analytics */}
      <SettingsSection title="Analytics">
        <SettingsCard to="/settings/analytics-overview" icon="📊" label="Analytics Overview" />
        <SettingsCard to="/analytics/funnel-builder" icon="🔀" label="Funnel Builder" />
        <SettingsCard to="/analytics/retention" icon="📈" label="Cohort Retention" />
        <SettingsCard to="/analytics/acquisition" icon="🎯" label="User Acquisition" />
        <SettingsCard to="/analytics/segmentation" icon="🧩" label="User Segmentation" />
        <SettingsCard to="/analytics/ml-insights" icon="🤖" label="ML Insights" />
        <SettingsCard to="/analytics/errors" icon="🐛" label="Error Investigation" />
        <SettingsCard to="/settings/gpt-prompt-events" icon="💬" label="GPT Prompt Usage" />
        <SettingsCard to="/settings/usage-events" icon="📉" label="Usage Events" />
        <SettingsCard to="/settings/webhook-monitor" icon="🔔" label="Webhook Monitor" />
        <SettingsCard to="/settings/payments-dashboard" icon="💰" label="Payments Dashboard" />
        <SettingsCard to="/settings/data-retention" icon="🗄️" label="Data Retention" />
      </SettingsSection>

      {/* System */}
      <SettingsSection title="System">
        <SettingsCard to="/settings/system-stats" icon="🩺" label="System Stats" />
      </SettingsSection>

      {/* Administration */}
      <SettingsSection title="Administration">
        <SettingsCard to="/settings/emails" icon="✉️" label="Emails" />
        <SettingsCard to="/settings/organizations" icon="🏢" label="Organizations" />
        <SettingsCard to="/settings/roles" icon="🔑" label="Roles" />
        <SettingsCard to="/settings/users" icon="👥" label="Users" />
        <SettingsCard to="/settings/users/invite" icon="📨" label="Invite User" />
        <SettingsCard to="/settings/grant-pro-access" icon="⭐" label="Grant Pro Access" />
        <SettingsCard to="/settings/impersonate-user" icon="🕵️" label="Sign In As User" />
      </SettingsSection>
    </div>
  );

  return (
    <div className="settings-container settings-theme-page">
      <Routes>
        {/* All routes are now accessible to any admin user */}
        
        {/* Document Types */}
        <Route path="document-types" element={<ProtectedRoute><DocumentTypeList /></ProtectedRoute>} />
        <Route path="document-types/create" element={<ProtectedRoute><DocumentTypeEdit /></ProtectedRoute>} />
        <Route path="document-types/:id" element={<ProtectedRoute><DocumentTypeEdit /></ProtectedRoute>} />
        <Route path="document-types/:id/assign-fields" element={<ProtectedRoute><AssignFieldsToDocumentType /></ProtectedRoute>} />

        {/* Metadata Definitions */}
        <Route path="document-metadata-definitions" element={<ProtectedRoute><MetadataDefinitionList /></ProtectedRoute>} />
        <Route path="document-metadata-definitions/create" element={<ProtectedRoute><MetadataDefinitionEdit /></ProtectedRoute>} />
        <Route path="document-metadata-definitions/:id" element={<ProtectedRoute><MetadataDefinitionEdit /></ProtectedRoute>} />

        {/* Endeavor Types */}
        <Route path="endeavor-types" element={<ProtectedRoute><EndeavorTypeList /></ProtectedRoute>} />
        <Route path="endeavor-types/create" element={<ProtectedRoute><EndeavorTypeEdit /></ProtectedRoute>} />
        <Route path="endeavor-types/:id" element={<ProtectedRoute><EndeavorTypeEdit /></ProtectedRoute>} />

        {/* Endeavor Metadata Definitions */}
        <Route path="endeavor-metadata-definitions" element={<ProtectedRoute><EndeavorMetadataDefinitionList /></ProtectedRoute>} />
        <Route path="endeavor-metadata-definitions/create" element={<ProtectedRoute><EndeavorMetadataDefinitionEdit /></ProtectedRoute>} />
        <Route path="endeavor-metadata-definitions/:id" element={<ProtectedRoute><EndeavorMetadataDefinitionEdit /></ProtectedRoute>} />

        {/* Event Types - FIXED ROUTES */}
        <Route path="event-types" element={<ProtectedRoute><EventTypeList /></ProtectedRoute>} />
        <Route path="event-types/create" element={<ProtectedRoute><CreateEditEventType /></ProtectedRoute>} />
        <Route path="event-types/edit/:id" element={<ProtectedRoute><CreateEditEventType /></ProtectedRoute>} />

        {/* Event Metadata Definitions */}
        <Route path="event-metadata-definitions" element={<ProtectedRoute><EventMetadataDefinitionList /></ProtectedRoute>} />
        <Route path="event-metadata-definitions/create" element={<ProtectedRoute><EventMetadataDefinitionEdit /></ProtectedRoute>} />
        <Route path="event-metadata-definitions/:id" element={<ProtectedRoute><EventMetadataDefinitionEdit /></ProtectedRoute>} />

        {/* General Settings */}
        <Route path="language" element={<ProtectedRoute><LanguageSettings /></ProtectedRoute>} />
  <Route path="emails" element={<ProtectedRoute><EmailList /></ProtectedRoute>} />
  <Route path="emails/create" element={<ProtectedRoute><EmailEdit /></ProtectedRoute>} />
  <Route path="emails/:id" element={<ProtectedRoute><EmailEdit /></ProtectedRoute>} />

        {/* Organizations */}
        <Route path="organizations" element={<ProtectedRoute><OrganizationList /></ProtectedRoute>} />
        <Route path="organizations/create" element={<ProtectedRoute><CreateOrganization /></ProtectedRoute>} />
        <Route path="organizations/:id" element={<ProtectedRoute><CreateOrganization /></ProtectedRoute>} />
        <Route path="organizations/:organizationId/members" element={<ProtectedRoute><OrganizationMembersPage /></ProtectedRoute>} />

        {/* Roles */}
        <Route path="roles" element={<ProtectedRoute><RoleList /></ProtectedRoute>} />
        <Route path="roles/create" element={<ProtectedRoute><CreateRole /></ProtectedRoute>} />
        <Route path="roles/:id" element={<ProtectedRoute><CreateRole /></ProtectedRoute>} />
        <Route path="roles/:id/permissions" element={<ProtectedRoute><div>Role Permissions (Coming Soon)</div></ProtectedRoute>} />

        {/* Users */}
        <Route path="users" element={<ProtectedRoute><UserList /></ProtectedRoute>} />
        <Route path="users/create" element={<ProtectedRoute><CreateUser /></ProtectedRoute>} />
        <Route path="users/invite" element={<ProtectedRoute><InviteUserPage /></ProtectedRoute>} />
        <Route path="users/:id" element={<ProtectedRoute><CreateUser /></ProtectedRoute>} />
        <Route path="users/:id/roles" element={<ProtectedRoute><div>User Roles (Coming Soon)</div></ProtectedRoute>} />
        <Route path="users/:userId/organizations" element={<ProtectedRoute><UserOrganizationsPage /></ProtectedRoute>} />
        <Route path="users/:id/reset-password" element={<ProtectedRoute><div>Reset Password (Coming Soon)</div></ProtectedRoute>} />

  {/* GPT Prompt Events Dashboard */}
  <Route path="analytics-overview" element={<ProtectedRoute><AnalyticsOverview /></ProtectedRoute>} />
  <Route path="correlation/:correlationId" element={<ProtectedRoute><CorrelationDetail /></ProtectedRoute>} />
  <Route path="gpt-prompt-events" element={<ProtectedRoute><GptPromptEventsDashboard /></ProtectedRoute>} />
  <Route path="usage-events" element={<ProtectedRoute><UsageEventsDashboard /></ProtectedRoute>} />
  <Route path="usage-events/user/:userId" element={<ProtectedRoute><UserUsageEventsDashboard /></ProtectedRoute>} />
  <Route path="webhook-monitor" element={<ProtectedRoute><WebhookMonitor /></ProtectedRoute>} />
  {/* Payments dashboard routes (alias paths) */}
  <Route path="payments-analytics" element={<ProtectedRoute><PaymentsAdminDashboard /></ProtectedRoute>} />
  <Route path="payments-dashboard" element={<ProtectedRoute><PaymentsAdminDashboard /></ProtectedRoute>} />
  {/* Grant Pro Access */}
  <Route path="grant-pro-access" element={<ProtectedRoute><AdminGrantProAccess /></ProtectedRoute>} />
  {/* Data Retention */}
  <Route path="data-retention" element={<ProtectedRoute><DataRetentionSettings /></ProtectedRoute>} />
  {/* System Stats */}
  <Route path="system-stats" element={<ProtectedRoute><AdminSystemStats /></ProtectedRoute>} />
  {/* Impersonate User */}
  <Route path="impersonate-user" element={<ProtectedRoute><ImpersonateUser /></ProtectedRoute>} />

        {/* Fallback route - show settings hub */}
        <Route path="*" element={<SettingsHub />} />
      </Routes>
    </div>
  );
}

export default Settings;
